import { memo } from 'react';

import PropTypes from 'prop-types';
import { trans } from '@spotahome/soyuz/client';
import SoyuzAnalytics from '@spotahome/soyuz-analytics';

import {
  BestChoiceLabelCircledIcon,
  InstantBookingLabelCircledIcon,
  BillsLabelCircledIcon,
  PlusLabelCircledIcon,
  LongTermLabelCircledIcon,
  LockRoundedIcon,
  Button,
  CHANNELS,
  useMediatorModalHandle
} from '@spotahome/ui-library';
import openDepositProtectionModal from '@spotahome/ui-library/src/tenant/components/DepositProtectionModal';

import PiggyBankLabelCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/PiggyBankLabelCircledIcon';
import HouseLabelCircledIcon from '@spotahome/ui-library/src/tenant/components/icons/HouseLabelCircledIcon';

import LongTermDescription from '@spotahome/ui-library/src/tenant/components/LongTermDescription';
import PlusLabelDescription from '@spotahome/ui-library/src/tenant/components/PlusLabelDescription';
import VerificationIconWrapper from '@spotahome/ui-library/src/tenant/components/VerificationIconWrapper';
import CheckedIconPhoto from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/ChekedIconPhoto';
import CheckedInfo from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/CheckedInfo';
import LLInfo from '@spotahome/ui-library/src/tenant/components/ListingLabel/LLInfo';
import TitleChecked from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/TitleChecked';
import CheckedDescription from '@spotahome/ui-library/src/tenant/components/ListingLabel/checkedInfo/CheckedDescription';
import Scoring from '@spotahome/ui-library/src/tenant/components/Scoring';

import { HomeCheckerShape } from '@spotahome/ui-library/src/tenant/shapes/HomeChecker';
import { LandlordScoringShape } from '@spotahome/ui-library/src/tenant/constants/shapes';

import instantBookingShape from '@spotahome/marketplace-common/src/shapes/instantBooking';

import { useLandlordInformation } from '../../context/LandlordInformationContext';

import ListingLabel from './ListingLabel';
import InstantBookingLabelDescription from './InstantBookingLabelDescription';

import './ListingLabels.scss';

const EMPTY_OBJECT = {};

const ListingLabels = ({
  isChecked,
  hasNoDeposit,
  hasDepositProtection = true,
  instantBooking = EMPTY_OBJECT,
  allBillsIncluded,
  someBillsIncluded,
  isBestChoice,
  isPlusPlan,
  allowsPermanentContract,
  homechecker = null,
  verificationDate = null,
  landlordType,
  landlordId,
  landlordScoring = EMPTY_OBJECT
}) => {
  const { landlordInformation, loading } = useLandlordInformation(landlordId);

  const { handleModalOpen } = useMediatorModalHandle({
    channel: CHANNELS.OPEN_VERIFICATION_MODAL
  });

  const getTitle = title => {
    return <span>{title}</span>;
  };

  const handleOnClick = () => {
    SoyuzAnalytics.sendGA4Event('ll-info-preview-click-listing', {});
    const llSection = document.querySelector(
      '[data-sticky-in-navigation="aboutLandlord"]'
    );
    window.scrollTo({
      top: llSection.getBoundingClientRect().top - 70 + window.scrollY,
      behavior: 'smooth'
    });
  };

  const shouldShowScoring =
    landlordScoring?.score && landlordScoring?.totalBookingsClosed;

  const labelsDict = [
    {
      id: 'depositProtected',
      renderIcon: () => <LockRoundedIcon />,
      renderTitle: () =>
        getTitle(trans('listing.labels.deposit-protected.title')),
      renderDescription: () => (
        <p className="listing-labels__deposit-body">
          {trans('listing.labels.deposit-protected.description')}{' '}
          <Button
            color="link"
            onClick={() => {
              SoyuzAnalytics.sendGA4Event('deposit-protection-more', {});
              openDepositProtectionModal();
            }}
          >
            {trans('home-ll.cta')} {/* More information (reused)*/}
          </Button>
        </p>
      ),
      enabled: !hasNoDeposit && hasDepositProtection
    },
    {
      id: 'checked',
      renderIcon: () => {
        return <CheckedIconPhoto photoUrl={homechecker?.photoUrl} isVerified />;
      },
      renderTitle: () => <TitleChecked firstName={homechecker?.firstName} />,
      renderInfo: () => <CheckedInfo verificationDate={verificationDate} />,
      renderDescription: () => (
        <CheckedDescription
          homechecker={homechecker}
          verificationDate={verificationDate}
          onMoreDetailsClick={handleModalOpen}
          showButtonModal
        />
      ),
      enabled: isChecked
    },
    {
      id: 'noDeposit',
      renderIcon: () => <PiggyBankLabelCircledIcon />,
      renderTitle: () => getTitle(trans('listing.labels.noDeposit.title')),
      renderDescription: () => trans('listing.labels.noDeposit.description'),
      enabled: hasNoDeposit
    },
    {
      id: 'instantBooking',
      renderIcon: () => <InstantBookingLabelCircledIcon />,
      renderTitle: () => getTitle(trans('listing.labels.instantBooking.title')),
      renderDescription: () => (
        <InstantBookingLabelDescription rules={instantBooking?.rules} />
      ),
      enabled: instantBooking?.isEnabled
    },
    {
      id: 'billsIncluded',
      renderIcon: () => <BillsLabelCircledIcon />,
      renderTitle: () => getTitle(trans('listing.labels.bills.all.title')),
      renderDescription: () => trans('listing.labels.bills.description'),
      enabled: allBillsIncluded
    },
    {
      id: 'someBillsIncluded',
      renderIcon: () => <BillsLabelCircledIcon />,
      renderTitle: () => getTitle(trans('listing.labels.bills.some.title')),
      renderDescription: () => trans('listing.labels.bills.some.description'),
      enabled: someBillsIncluded && !allBillsIncluded
    },
    {
      id: 'bestChoice',
      renderIcon: () => <BestChoiceLabelCircledIcon />,
      renderTitle: () => getTitle(trans('listing.labels.bestChoice.title')),
      renderDescription: () => trans('listing.labels.bestChoice.description'),
      enabled: isBestChoice
    },
    {
      id: 'plus',
      renderIcon: () => <PlusLabelCircledIcon />,
      renderTitle: () => getTitle(trans('Spotahome PLUS')),
      renderDescription: () => <PlusLabelDescription />,
      enabled: isPlusPlan
    },
    {
      id: 'longTerm',
      renderIcon: () => <LongTermLabelCircledIcon />,
      renderTitle: () => getTitle(trans('homecard.label.long-term')),
      renderDescription: () => <LongTermDescription />,
      enabled: allowsPermanentContract
    },
    {
      id: 'llInfo',
      renderIcon: () => (
        <VerificationIconWrapper>
          <HouseLabelCircledIcon />
        </VerificationIconWrapper>
      ),
      renderTitle: () => (
        <>
          {getTitle(trans('listing.landlord_info.verified_landlord'))}
          {shouldShowScoring && (
            <Scoring
              score={landlordScoring.score}
              totalReviews={landlordScoring.totalBookingsClosed}
              tooltipPosition="right"
            />
          )}
        </>
      ),
      renderInfo: () => (
        <LLInfo
          landlordInformation={landlordInformation}
          loading={loading}
          onClick={handleOnClick}
        >
          <Button color="link" onClick={handleModalOpen}>
            {trans('listing.verification.more-button')}
          </Button>
        </LLInfo>
      ),
      renderDescription: () => {},
      enabled: !!landlordType
    }
  ];

  return (
    <>
      <ul className="listing-labels__list">
        {labelsDict.map(
          ({
            renderTitle,
            renderDescription,
            renderIcon,
            renderInfo = () => {},
            enabled,
            id
          }) =>
            enabled && (
              <ListingLabel
                key={id}
                icon={renderIcon()}
                title={renderTitle()}
                info={renderInfo()}
              >
                {renderDescription()}
              </ListingLabel>
            )
        )}
      </ul>
    </>
  );
};

ListingLabels.propTypes = {
  isChecked: PropTypes.bool.isRequired,
  hasNoDeposit: PropTypes.bool.isRequired,
  hasDepositProtection: PropTypes.bool,
  instantBooking: PropTypes.shape(instantBookingShape),
  allBillsIncluded: PropTypes.bool.isRequired,
  someBillsIncluded: PropTypes.bool.isRequired,
  isBestChoice: PropTypes.bool.isRequired,
  isPlusPlan: PropTypes.bool.isRequired,
  allowsPermanentContract: PropTypes.bool.isRequired,
  homechecker: PropTypes.shape(HomeCheckerShape),
  verificationDate: PropTypes.string,
  landlordType: PropTypes.oneOf(['professional', 'private']).isRequired,
  landlordId: PropTypes.string.isRequired,
  landlordScoring: PropTypes.shape(LandlordScoringShape)
};

export default memo(ListingLabels);
